import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/kit';
import { FormDialog, PickSelect } from '@/components/inputs';
import { AUDIENCES, BROADCAST_KINDS } from '@/shared/constants';
import { AUDIENCE_LABELS, KIND_HINTS, audienceOf } from '@/shared/audience';
import { committed, displayNameOf, logActivity } from '@/shared/data';

const KIND_LABELS = { Service: 'Service notice', Promo: 'Offer or promo' };

function fromBroadcast(b, sender) {
  return {
    Name: b?.Name ?? '',
    kind_c: b?.kind_c || 'Service',
    audience_c: b?.audience_c || 'Active',
    body_c: b?.body_c ?? '',
    recipients_c: b?.recipients_c != null ? String(b.recipients_c) : '',
    sent_by_c: b?.sent_by_c ?? sender,
  };
}

/** Log or edit an email sent to a group of customers. New ones count the audience as you pick it. */
export default function BroadcastDialog({ open, onOpenChange, broadcast, onSaved }) {
  const { user } = useAuth();
  const sender = displayNameOf(user);
  const editing = !!broadcast;
  const [form, setForm] = useState(fromBroadcast(broadcast, sender));
  const [saving, setSaving] = useState(false);
  const [counting, setCounting] = useState(false);

  useEffect(() => {
    if (open) setForm(fromBroadcast(broadcast, sender));
  }, [open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key) => (e) => set(key)(e.target.value);

  async function count(audience = form.audience_c, kind = form.kind_c) {
    setCounting(true);
    try {
      const list = await audienceOf(audience, kind);
      setForm((f) => ({ ...f, recipients_c: String(list.length) }));
    } catch (err) {
      toast.error(err?.message || 'Could not count the audience');
    } finally {
      setCounting(false);
    }
  }

  useEffect(() => {
    if (open && !editing) count(form.audience_c, form.kind_c);
  }, [open, form.audience_c, form.kind_c]);

  async function submit() {
    if (form.Name.trim().length < 3) {
      toast.error('Add a subject');
      return;
    }
    if (form.body_c.trim().length < 10) {
      toast.error('Write the message');
      return;
    }
    const recipients = Number(form.recipients_c);
    if (!Number.isInteger(recipients) || recipients < 0) {
      toast.error('Recipients is a whole number');
      return;
    }
    const fields = {
      Name: form.Name.trim(),
      kind_c: form.kind_c,
      audience_c: form.audience_c,
      body_c: form.body_c.trim(),
      recipients_c: recipients,
      sent_by_c: form.sent_by_c.trim() || sender,
    };
    setSaving(true);
    try {
      const res = editing
        ? await sdk.table('broadcasts_c').update({ Id: broadcast.Id, ...fields })
        : await sdk.table('broadcasts_c').create(fields);
      const saved = committed(res, editing ? 'Broadcast updated' : 'Broadcast saved');
      if (!saved) return;
      if (!editing) logActivity('Email', 'Broadcast', fields.audience_c, `${fields.kind_c}: ${fields.Name} (${recipients})`);
      onOpenChange(false);
      setForm(fromBroadcast(null, sender));
      onSaved?.(saved);
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      wide
      title={editing ? 'Edit broadcast' : 'New broadcast'}
      description={editing ? undefined : 'Save it here, then open it to copy the recipient list into your mail app.'}
      onSubmit={submit}
      saving={saving}
      submitLabel={editing ? 'Save changes' : 'Save broadcast'}
      busyLabel="Saving…"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Kind" htmlFor="bc-kind" hint={KIND_HINTS[form.kind_c]}>
          <PickSelect id="bc-kind" value={form.kind_c} onChange={set('kind_c')} values={BROADCAST_KINDS} labels={KIND_LABELS} placeholder="Service or promo" />
        </Field>
        <Field label="Audience" htmlFor="bc-audience">
          <PickSelect id="bc-audience" value={form.audience_c} onChange={set('audience_c')} values={AUDIENCES} labels={AUDIENCE_LABELS} placeholder="Who it goes to" />
        </Field>
      </div>
      <Field label="Subject" htmlFor="bc-subject">
        <Input id="bc-subject" value={form.Name} onChange={text('Name')} placeholder="Maintenance on 4 November" maxLength={150} />
      </Field>
      <Field label="Message" htmlFor="bc-body">
        <Textarea
          id="bc-body"
          rows={7}
          value={form.body_c}
          onChange={text('body_c')}
          placeholder="We are upgrading servers on Tuesday between 03:00 and 04:00 UTC. Lines may drop for a few minutes."
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Recipients" htmlFor="bc-recipients" hint="Counted from the audience. Change it if you sent to fewer.">
          <div className="flex gap-2">
            <Input
              id="bc-recipients"
              type="number"
              min="0"
              step="1"
              value={form.recipients_c}
              onChange={text('recipients_c')}
              placeholder="12"
              className="tabular-nums"
            />
            <Button type="button" variant="outline" onClick={() => count()} disabled={counting}>
              <ApperIcon name="Users" size={16} />
              {counting ? 'Counting…' : 'Count'}
            </Button>
          </div>
        </Field>
        <Field label="Sent by" htmlFor="bc-sender">
          <Input id="bc-sender" value={form.sent_by_c} onChange={text('sent_by_c')} placeholder="Vesper Hollow" />
        </Field>
      </div>
    </FormDialog>
  );
}
